<template>
    <div>
        <div class="tiptap-editor" :style="maxHeight ? { '--editor-max-height': maxHeight } : {}">
            <div v-if="editor" class="toolbar">
                <button
                    :class="{ 'is-active': editor.isActive('bold') }"
                    type="button"
                    @click="editor.chain().focus().toggleBold().run()"
                >
                    <i class="mdi mdi-format-bold"></i>
                </button>
                <button
                    :class="{ 'is-active': editor.isActive('italic') }"
                    type="button"
                    @click="editor.chain().focus().toggleItalic().run()"
                >
                    <i class="mdi mdi-format-italic"></i>
                </button>
                <button
                    :class="{ 'is-active': editor.isActive('underline') }"
                    type="button"
                    @click="editor.chain().focus().toggleUnderline().run()"
                >
                    <i class="mdi mdi-format-underline"></i>
                </button>
                <span class="separator">|</span>
                <button :class="{ 'is-active': editor.isActive('link') }" type="button" @click="openLinkModal">
                    <i class="mdi mdi-link"></i>
                </button>
                <button v-if="!without.includes('image')" type="button" disabled title="Drag files to the editor">
                    <i class="mdi mdi-image"></i>
                </button>
                <button
                    title="iFrame"
                    :class="{ 'is-active': editor.isActive('iframe') }"
                    type="button"
                    @click="openIframeModal()"
                >
                    <i class="mdi mdi-web"></i>
                </button>
                <span class="separator">|</span>
                <button
                    :class="{ 'is-active': editor.isActive('heading', { level: 1 }) }"
                    type="button"
                    @click="editor.chain().focus().toggleHeading({ level: 1 }).run()"
                >
                    <i class="mdi mdi-format-header-1"></i>
                </button>
                <button
                    :class="{ 'is-active': editor.isActive('heading', { level: 2 }) }"
                    type="button"
                    @click="editor.chain().focus().toggleHeading({ level: 2 }).run()"
                >
                    <i class="mdi mdi-format-header-2"></i>
                </button>
                <button
                    :class="{ 'is-active': editor.isActive('heading', { level: 3 }) }"
                    type="button"
                    @click="editor.chain().focus().toggleHeading({ level: 3 }).run()"
                >
                    <i class="mdi mdi-format-header-3"></i>
                </button>
                <span class="separator">|</span>
                <button
                    :class="{ 'is-active': editor.isActive('bulletList') }"
                    type="button"
                    @click="editor.chain().focus().toggleBulletList().run()"
                >
                    <i class="mdi mdi-format-list-bulleted"></i>
                </button>
                <button
                    :class="{ 'is-active': editor.isActive('orderedList') }"
                    type="button"
                    @click="editor.chain().focus().toggleOrderedList().run()"
                >
                    <i class="mdi mdi-format-list-numbered"></i>
                </button>
                <button
                    :class="{ 'is-active': editor.isActive('code') }"
                    @click="editor.chain().focus().toggleCode().run()"
                >
                    <i class="mdi mdi-code-tags"></i>
                </button>
                <span class="separator">|</span>
                <button
                    v-if="!props.without.includes('table')"
                    type="button"
                    @click="editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()"
                >
                    <i class="mdi mdi-table"></i>
                </button>
                <button
                    :class="{ 'is-active': editor.isActive('blockquote') }"
                    type="button"
                    @click="editor.chain().focus().toggleBlockquote().run()"
                >
                    <i class="mdi mdi-format-quote-close"></i>
                </button>
                <span class="separator">|</span>
                <button type="button" @click="editor.chain().focus().undo().run()">
                    <i class="mdi mdi-undo"></i>
                </button>
                <button type="button" @click="editor.chain().focus().redo().run()">
                    <i class="mdi mdi-redo"></i>
                </button>
            </div>
            <EditorContent :editor />
        </div>
        <Modal ref="linkModal" title="Insert Link">
            <form @submit.prevent="setLink($event.target.link.value)">
                <Textbox
                    type="text"
                    placeholder="https://example.com"
                    icon="link"
                    name="link"
                    @keyup.enter="(e) => setLink(e.target.value)"
                >
                    <template #after>
                        <Button button-type="submit" type="is-primary" class="tw-ml-4">Insert Link</Button>
                    </template>
                </Textbox>
            </form>
        </Modal>

        <Modal ref="iframeModal" title="Insert iFrame">
            <form @submit.prevent="setIframe($event.target.iframe.value)">
                <Textbox
                    type="text"
                    placeholder="https://example.com/abc"
                    icon="iframe"
                    name="iframe"
                    @keyup.enter="(e) => setIframe(e.target.value)"
                >
                    <template #after>
                        <Button button-type="submit" type="is-primary" class="tw-ml-4">Insert iFrame</Button>
                    </template>
                </Textbox>
            </form>
        </Modal>
    </div>
</template>

<script setup>
import { ref, watch, onMounted, onBeforeUnmount, nextTick, useTemplateRef } from 'vue';
import { Editor, EditorContent } from '@tiptap/vue-3';
import { StarterKit } from '@tiptap/starter-kit';
import { Table } from '@tiptap/extension-table';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { TableRow } from '@tiptap/extension-table-row';
import { TextAlign } from '@tiptap/extension-text-align';
import { FileHandler } from '@tiptap/extension-file-handler';
import Modal from '@/js/components/Modal.vue';
import Textbox from '@/js/components/controls/Textbox.vue';
import Button from '@/js/components/controls/Button.vue';
import { Node } from '@tiptap/core';

const IframeExtension = Node.create({
    name: 'iframe',
    group: 'block',
    atom: true,
    addOptions() {
        return {
            allowFullscreen: true,
            HTMLAttributes: {
                class: 'help-article-iframe-wrapper',
            },
        };
    },
    addAttributes() {
        return {
            src: {
                default: null,
            },
            frameborder: {
                default: 0,
            },
            allowfullscreen: {
                default: this.options.allowFullscreen,
                parseHTML: () => this.options.allowFullscreen,
            },
        };
    },
    parseHTML() {
        return [
            {
                tag: 'iframe',
            },
        ];
    },
    renderHTML({ HTMLAttributes }) {
        return ['div', this.options.HTMLAttributes, ['iframe', HTMLAttributes]];
    },
    addCommands() {
        return {
            setIframe:
                (options) =>
                ({ tr, dispatch }) => {
                    const { selection } = tr;
                    const node = this.type.create(options);

                    if (dispatch) {
                        tr.replaceRangeWith(selection.from, selection.to, node);
                    }

                    return true;
                },
        };
    },
});

const props = defineProps({
    modelValue: {
        type: String,
        default: '',
    },
    without: {
        type: Array,
        default: () => [],
    },
    maxHeight: {
        type: String,
        default: null,
    },
});
const linkModal = useTemplateRef('linkModal');
const iframeModal = useTemplateRef('iframeModal');

const emit = defineEmits(['update:modelValue', 'ready']);

const editor = ref(null);

watch(
    () => props.modelValue,
    (value) => {
        if (!editor.value) return;

        const isSame = editor.value.getHTML() === value;
        if (isSame) {
            return;
        }
        editor.value.commands.setContent(value, false);
    }
);

onMounted(() => {
    const extensions = [
        Table.configure({
            resizable: true,
        }),
        TableCell,
        TableHeader,
        TableRow,
        StarterKit.configure({
            heading: {
                levels: [1, 2, 3],
            },
        }),
        IframeExtension,

        TextAlign.configure({
            types: ['heading', 'paragraph'],
        }),
        FileHandler.configure({
            allowedMimeTypes: ['image/png', 'image/jpeg', 'image/gif', 'image/webp'],
            onDrop: (currentEditor, files, pos) => {
                files.forEach((file) => {
                    const fileReader = new FileReader();

                    fileReader.readAsDataURL(file);
                    fileReader.onload = () => {
                        currentEditor
                            .chain()
                            .insertContentAt(pos, {
                                type: 'image',
                                attrs: {
                                    src: fileReader.result,
                                },
                            })
                            .focus()
                            .run();
                    };
                });
            },
            onPaste: (currentEditor, files) => {
                files.forEach((file) => {
                    const fileReader = new FileReader();

                    fileReader.readAsDataURL(file);
                    fileReader.onload = () => {
                        currentEditor
                            .chain()
                            .insertContentAt(currentEditor.state.selection.anchor, {
                                type: 'image',
                                attrs: {
                                    src: fileReader.result,
                                },
                            })
                            .focus()
                            .run();
                    };
                });
            },
        }),
    ];

    editor.value = new Editor({
        extensions,
        content: props.modelValue,
        onUpdate: () => {
            emit('update:modelValue', editor.value.getHTML());
        },
        onCreate: () => {
            emit('ready', editor.value);
        },
    });
});

onBeforeUnmount(() => {
    if (editor.value) {
        editor.value.destroy();
    }
});

const openLinkModal = async () => {
    linkModal.value.open();
    await nextTick();
    linkModal.value.$el.querySelector('input').focus();
};

const setLink = (value) => {
    editor.value.chain().focus().extendMarkRange('link').setLink({ href: value }).run();
    linkModal.value.close();
};

const openIframeModal = async () => {
    iframeModal.value.open();
    await nextTick();
    iframeModal.value.$el.querySelector('input').focus();
};

const setIframe = (url) => {
    if (url) {
        editor.value.chain().focus().setIframe({ src: url }).run();
    }
    iframeModal.value.close();
};

defineExpose({
    editor,
});
</script>

<style scoped>
.tiptap-editor {
    border: 1px solid #e2e8f0;
    border-radius: 0.375rem;
}

.toolbar {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
    padding: 0.5rem;
    border-bottom: 1px solid #e2e8f0;
    background-color: #f8fafc;
}

.toolbar button {
    padding: 0.375rem 0.5rem;
    border: 1px solid #e2e8f0;
    background-color: white;
    border-radius: 0.25rem;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
}

.toolbar button:hover {
    background-color: #f1f5f9;
}

.toolbar button.is-active {
    background-color: #3b82f6;
    color: white;
    border-color: #3b82f6;
}

.toolbar .separator {
    align-self: center;
    color: #cbd5e1;
    padding: 0 0.25rem;
}

:deep(.tiptap) {
    min-height: var(--editor-max-height, 60vh);
    max-height: var(--editor-max-height, none);
    overflow-y: auto;
    padding: 1rem;
    outline: none;
}

:deep(.tiptap:focus) {
    outline: none;
}

:deep(.tiptap > ul),
:deep(.tiptap > ol) {
    padding-left: 1.5em;
}

:deep(.tiptap h1) {
    font-size: 2em;
    font-weight: bold;
    margin-top: 0.67em;
    margin-bottom: 0.67em;
}

:deep(.tiptap h2) {
    font-size: 1.5em;
    font-weight: bold;
    margin-top: 0.75em;
    margin-bottom: 0.75em;
}

:deep(.tiptap h3) {
    font-size: 1.17em;
    font-weight: bold;
    margin-top: 0.83em;
    margin-bottom: 0.83em;
}

:deep(.tiptap p) {
    margin-top: 1em;
    margin-bottom: 1em;
}

:deep(.tiptap a) {
    color: #3b82f6;
    text-decoration: underline;
}

:deep(.tiptap blockquote) {
    border-left: 3px solid #e2e8f0;
    padding-left: 1rem;
    margin-left: 0;
    font-style: italic;
}

:deep(.tiptap table) {
    border-collapse: collapse;
    margin: 1em 0;
    overflow: hidden;
    table-layout: fixed;
    width: 100%;
}

:deep(.tiptap table td),
:deep(.tiptap table th) {
    border: 1px solid #e2e8f0;
    padding: 0.5rem;
    position: relative;
    vertical-align: top;
}

:deep(.tiptap table th) {
    background-color: #f8fafc;
    font-weight: bold;
    text-align: left;
}

:deep(.tiptap .tiptap-image) {
    max-width: 100%;
    height: auto;
}

:deep(.tiptap ul) {
    list-style-type: disc;
}

:deep(.tiptap ol) {
    list-style-type: decimal;
}
</style>
